import { useEffect, useRef, useState } from 'preact/hooks'
import { useSignalEffect, type ReadonlySignal } from '@preact/signals'
import type { StorableItem } from '../store/ResultItemsListStore'

export function diffSnapshots(prev: Map<string, number>, current: Map<string, number>): string[] {
  const changed: string[] = []
  for (const [id, qty] of current) {
    const prevQty = prev.get(id)
    if (prevQty === undefined || qty > prevQty) {
      changed.push(id)
    }
  }
  return changed
}

function buildSnapshot(items: StorableItem[]): Map<string, number> {
  const snapshot = new Map<string, number>()
  for (const item of items) {
    snapshot.set(item.id, item.quantity.value)
  }
  return snapshot
}

export function useRecentlyAddedIds(items: ReadonlySignal<StorableItem[]>, durationMs: number): Set<string> {
  const [recentlyAdded, setRecentlyAdded] = useState<Set<string>>(new Set())
  const prevSnapshot = useRef<Map<string, number>>(new Map())
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  // Reading items.value and (inside buildSnapshot) item.quantity.value
  // auto-subscribes this effect to both the list and every per-item
  // quantity signal, so quantity bumps re-fire without an explicit dep.
  useSignalEffect(() => {
    const current = buildSnapshot(items.value)

    if (prevSnapshot.current.size > 0 && current.size === 0) {
      for (const timer of timers.current.values()) {
        clearTimeout(timer)
      }
      timers.current.clear()
      setRecentlyAdded(new Set())
      prevSnapshot.current = current
      return
    }

    const changed = diffSnapshots(prevSnapshot.current, current)
    prevSnapshot.current = current

    if (changed.length === 0) return

    setRecentlyAdded((prev) => {
      const next = new Set(prev)
      for (const id of changed) next.add(id)
      return next
    })

    for (const id of changed) {
      const existing = timers.current.get(id)
      if (existing) clearTimeout(existing)
      const timer = setTimeout(() => {
        timers.current.delete(id)
        setRecentlyAdded((prev) => {
          if (!prev.has(id)) return prev
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      }, durationMs)
      timers.current.set(id, timer)
    }
  })

  useEffect(() => {
    return () => {
      for (const timer of timers.current.values()) {
        clearTimeout(timer)
      }
      timers.current.clear()
    }
  }, [])

  return recentlyAdded
}
